"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import type { Dictionary } from "@/i18n/dictionaries";
import type { Locale } from "@/i18n/config";
import type { BranchOption } from "@/lib/data/booking";
import { branchName, studioName } from "@/lib/data/types";
import { setBookingSelection } from "@/lib/booking-selection";
import { SmartImage } from "@/components/smart-image";

interface Props {
  locale: Locale;
  dict: Dictionary;
  branches: BranchOption[];
  defaultBranchId?: string;
}

export function BranchStudioPicker({ locale, dict, branches, defaultBranchId }: Props) {
  const router = useRouter();
  const [branchId, setBranchId] = useState<string | null>(
    defaultBranchId ?? null,
  );

  const selectedBranch =
    branchId != null ? branches.find((b) => b.id === branchId) ?? null : null;

  const goToStudio = (productId: string | null, studioId: string) => {
    if (!productId || !selectedBranch) return;
    setBookingSelection({ branchId: selectedBranch.id, studioId });
    router.push(`/${locale}/booking/${productId}?studio=${studioId}`);
  };

  const chooseBranch = (id: string | null) => {
    if (id) setBookingSelection({ branchId: id, studioId: undefined });
    setBranchId(id);
  };

  return (
    <div className="mx-auto max-w-5xl px-5 pt-32 pb-24 sm:px-8">
      <header className="mb-12">
        <p className="label-caps text-[0.625rem] text-faint">{dict.booking.selectBranchEyebrow}</p>
        <h1 className="font-display mt-3 text-3xl text-strong sm:text-4xl">
          {dict.booking.selectBranch}
        </h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-body">
          {dict.booking.selectBranchDesc}
        </p>
      </header>

      {branches.length === 0 ? (
        <p className="text-sm text-faint">{dict.booking.noBranches}</p>
      ) : (
        <div className="space-y-12">
          {/* Branch selection */}
          <section aria-labelledby="branch-heading">
            <div className="mb-5 flex items-center justify-between">
              <h2 id="branch-heading" className="font-display text-xl text-strong">
                {dict.booking.branches}
              </h2>
              {branchId && (
                <button
                  type="button"
                  onClick={() => chooseBranch(null)}
                  className="text-xs text-accent-deep underline-offset-4 hover:underline"
                >
                  {dict.booking.changeBranch}
                </button>
              )}
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {(branchId ? branches.filter((b) => b.id === branchId) : branches).map(
                (branch) => {
                  const active = branch.id === branchId;
                  const bName = branchName(branch, locale);
                return (
                  <button
                    key={branch.id}
                    type="button"
                    onClick={() => chooseBranch(active ? null : branch.id)}
                    className={`group flex flex-col overflow-hidden rounded-lg border bg-paper text-left transition ${
                      active
                        ? "border-strong shadow-[0_28px_70px_-38px_rgba(35,33,29,0.35)]"
                        : "border-line hover:border-strong"
                    }`}
                  >
                    <div className="relative aspect-[16/9] w-full overflow-hidden bg-mist">
                      {branch.imageUrl ? (
                        <SmartImage
                          src={branch.imageUrl}
                          alt={bName}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                          fallback={<div className="h-full w-full bg-mist" />}
                        />
                      ) : <div className="h-full w-full bg-mist" />}
                    </div>
                    <div className="p-5">
                      <h3 className="font-display text-lg text-strong">{bName}</h3>
                      <p className="mt-1 text-xs text-faint">{branch.address}</p>
                      <p className="mt-3 text-xs text-body">
                        {branch.studios.length} {dict.booking.studiosCount}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </section>

          {/* Studio selection for the chosen branch */}
          {selectedBranch && (
            <section aria-labelledby="studio-heading">
              <h2 id="studio-heading" className="font-display mb-2 text-xl text-strong">
                {dict.booking.studiosIn} {branchName(selectedBranch, locale)}
              </h2>
              <p className="mb-5 text-xs text-faint">{selectedBranch.address}</p>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {selectedBranch.studios.map((studio) => {
                  const disabled = !studio.productId;
                  const sName = studioName(studio, locale);
                  return (
                    <button
                      key={studio.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => goToStudio(studio.productId, studio.id)}
                      className={`flex flex-col overflow-hidden rounded-lg border bg-paper text-left transition ${
                        disabled
                          ? "cursor-not-allowed border-line opacity-50"
                          : "border-line hover:border-strong"
                      }`}
                    >
                      <div className="relative aspect-[16/9] w-full overflow-hidden bg-mist">
                        {studio.imageUrl ? (
                          <SmartImage
                            src={studio.imageUrl}
                            alt={sName}
                            className="h-full w-full object-cover"
                            fallback={<div className="h-full w-full bg-mist" />}
                          />
                        ) : <div className="h-full w-full bg-mist" />}
                      </div>
                      <div className="p-5">
                        <h3 className="font-display text-lg text-strong">{sName}</h3>
                        <p className="mt-1 text-xs text-faint">
                          {dict.booking.capacity.replace("{n}", String(studio.capacity))}
                        </p>
                        {disabled && (
                          <p className="mt-3 text-xs text-faint">{dict.booking.noProduct}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
